import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Contact,
  MessageCircleQuestion,
  PartyPopper,
  PencilLine,
  Sparkles,
} from "@tamagui/lucide-icons";
import { action, runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Pressable } from "react-native";
import {
  Image,
  styled,
  Tabs,
  ToggleGroup,
  View,
  XStack,
  YStack,
} from "tamagui";
import type {
  PlaniraTemplate,
  PlaniraTemplateModule,
} from "../../templates/types";
import { useStores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import { PlaniraImageLookUp } from "../core/utils";
import { useCreatePlanFromTemplate } from "../templates/useCreatePlanFromTemplate";
import { Body } from "./Body";
import { Box } from "./Box";
import { Button } from "./Button";
import { type DotProps, DotWizard } from "./DotWizard";
import { Label, LabelTextarea } from "./Label";
import { Subtext } from "./Subtext";
import { TipsText } from "./TipsText";
import { Title } from "./Title";
import { UserCircle } from "./UserCircle";

type PlaniraTemplateModuleWithIndex = PlaniraTemplateModule & {
  keep: boolean;
  index: number;
};

type ModuleState = {
  modulesState: { modules: PlaniraTemplateModuleWithIndex[] };
};

const FineTunePlanTitle: React.FC<{
  plan: PlaniraTemplate;
  value: string;
  titleSuggestions: string[];
  setValue: (value: string) => void;
  onSubmit: () => void;
}> = observer(({ value, setValue, titleSuggestions, plan, onSubmit }) => {
  return (
    <YStack gap={"$large"}>
      <View>
        <YStack gap={"$small"}>
          <XStack gap="$small" alignItems="center">
            <PencilLine />
            <Title>Set your Planira title</Title>
          </XStack>
          <Body>
            First thing we need to do is to set a title. This is the first thing
            members of your Planira will see and should describe what you want
            to plan.
          </Body>
        </YStack>
      </View>
      <YStack gap={"$medium"}>
        <LabelTextarea
          preventNewLine
          type="border"
          placeholder={"Title..."}
          defaultValue={value}
          onChangeText={setValue}
          submitOnEnter
          onSubmitEditing={() => onSubmit()}
        />
        {plan.titleHelp && <TipsText text={plan.titleHelp} />}
      </YStack>

      {titleSuggestions.length > 0 && (
        <YStack gap={"$small"}>
          {titleSuggestions.map((suggestedTitle) => (
            <Pressable
              onPress={() => setValue(suggestedTitle)}
              key={suggestedTitle}
            >
              <Body muted textDecorationLine="underline">
                {suggestedTitle}
              </Body>
            </Pressable>
          ))}
        </YStack>
      )}
    </YStack>
  );
});

const ToggleGroupItem = styled(ToggleGroup.Item, {
  backgroundColor: "transparent",
  padding: "$medium",
  variants: {
    active: {
      true: {
        backgroundColor: "$primary",

        hoverStyle: {
          backgroundColor: "$primary",
        },

        focusStyle: {
          backgroundColor: "$primary",
        },
      },
    },
  },
});

const FineTunePlanCards: React.FC<
  ModuleState & {
    plan: PlaniraTemplate;
  }
> = observer(({ modulesState }) => {
  return (
    <YStack gap={"$large"}>
      <YStack gap={"$small"}>
        <XStack gap={"$small"} alignItems="center">
          <MessageCircleQuestion />
          <Title>Help us help you</Title>
        </XStack>
        <Body>
          Answer the questions below so we can help you with the perfect setup
          to start with. If you're not sure just move on to the next step!
        </Body>
      </YStack>

      {modulesState.modules
        .filter(
          (
            m,
          ): m is PlaniraTemplateModuleWithIndex & {
            questionOption: { question: string };
          } => !!m.questionOption,
        )
        .map((module) => (
          <XStack
            key={module.title}
            justifyContent="space-between"
            alignItems="center"
            gap="$small"
          >
            <YStack gap="$small" flex={1}>
              <Label>{module.questionOption.question}</Label>
              {module.questionOption.questionDescription && (
                <Subtext>{module.questionOption.questionDescription}</Subtext>
              )}
            </YStack>
            <ToggleGroup
              flexShrink={0}
              type="single"
              value={module.keep ? "false" : "true"}
              onValueChange={action((value) => {
                module.keep = value === "false";
              })}
              disablePassBorderRadius
              padding={1}
            >
              <ToggleGroupItem active={!module.keep} value={"true"}>
                <Body>Yes</Body>
              </ToggleGroupItem>
              <ToggleGroupItem active={module.keep} value={"false"}>
                <Body>No</Body>
              </ToggleGroupItem>
            </ToggleGroup>
          </XStack>
        ))}
    </YStack>
  );
});

const FineTunePlanDisplayname: React.FC<{
  plan: PlaniraTemplate;
  value: string;
  setValue: (value: string) => void;
  onSubmit: () => void;
}> = observer(({ value, setValue, onSubmit }) => {
  return (
    <YStack gap={"$large"}>
      <View>
        <YStack gap={"$small"}>
          <XStack gap="$small" alignItems="center">
            <Contact />
            <Title>Who are you?</Title>
          </XStack>
          <Body>
            This is what other members will see you as. It can be your real name
            or an alias, but the members you invite should be able to identify
            you by it.
          </Body>
        </YStack>
      </View>
      <XStack gap="$medium" alignItems="center">
        <UserCircle name={value} />
        <LabelTextarea
          preventNewLine
          type="border"
          defaultValue={value}
          placeholder="Your name or alias..."
          onChangeText={setValue}
          submitOnEnter
          onSubmitEditing={() => {
            onSubmit();
          }}
        />
      </XStack>
    </YStack>
  );
});

const FineTunePlanTabs: React.FC<
  ModuleState & {
    plan: PlaniraTemplate;
    currentTab: string;
    onTabChange: (value: string) => void;
    title: string;
    titleSuggestions: string[];
    setTitle: (value: string) => void;
    displayName: string;
    setDisplayName: (value: string) => void;
    onSubmit: () => void;
  }
> = observer(
  ({
    plan,
    modulesState,
    currentTab,
    title,
    titleSuggestions,
    displayName,
    onTabChange,
    setDisplayName,
    setTitle,
    onSubmit,
  }) => {
    const showFineTuneTab = false; // Disabled for now, testing inline questions!isQuickstart && !!modulesState.modules.length;

    const tabDots = React.useMemo<DotProps[]>(() => {
      const titleDone = currentTab !== "title" || !!title;
      const displayNameDone = currentTab === "displayName" && !!displayName;
      const dots = [
        {
          onPress: () => onTabChange("title"),
          completed: titleDone,
          step: titleDone ? undefined : 1,
          icon: titleDone ? CheckCircle : undefined,
        },
      ];

      if (showFineTuneTab) {
        const fineTuneDone = currentTab === "displayName";

        dots.push({
          onPress: () => onTabChange("cards"),
          completed: currentTab === "displayName",
          step: fineTuneDone ? undefined : 2,
          icon: fineTuneDone ? CheckCircle : undefined,
        });
      }

      dots.push({
        onPress: () => onTabChange("displayName"),
        completed: currentTab === "displayName",
        step: displayNameDone ? undefined : showFineTuneTab ? 3 : 2,
        icon: displayNameDone ? CheckCircle : undefined,
      });

      return dots;
    }, [onTabChange, currentTab, title, displayName]);

    return (
      <Tabs
        defaultValue="title"
        orientation="horizontal"
        flexDirection="column"
        overflow="hidden"
        value={currentTab}
        onValueChange={onTabChange}
      >
        <YStack gap="$medium">
          <DotWizard
            paddingHorizontal="$larger"
            paddingVertical="$medium"
            dots={tabDots}
          />
          <Box>
            <Tabs.Content value="title">
              <FineTunePlanTitle
                plan={plan}
                value={title}
                titleSuggestions={titleSuggestions}
                setValue={setTitle}
                onSubmit={onSubmit}
              />
            </Tabs.Content>

            {showFineTuneTab && (
              <Tabs.Content value="cards">
                <FineTunePlanCards plan={plan} modulesState={modulesState} />
              </Tabs.Content>
            )}

            <Tabs.Content value="displayName">
              <FineTunePlanDisplayname
                plan={plan}
                value={displayName}
                setValue={setDisplayName}
                onSubmit={onSubmit}
              />
            </Tabs.Content>
          </Box>
        </YStack>
      </Tabs>
    );
  },
);

export const CreatePlan: React.FC<{ planTemplateData: PlaniraTemplate }> =
  observer(({ planTemplateData }) => {
    const { t } = useTranslation();
    const { sessionStore } = useStores();

    const createPlan = useCreatePlanFromTemplate();
    const modulesState = useLocalObservable<{
      modules: PlaniraTemplateModuleWithIndex[];
    }>(() => ({ modules: [] }));

    const { suggestedTitle, titleSuggestions } = React.useMemo(() => {
      if (
        !planTemplateData?.suggestedTitles ||
        planTemplateData.suggestedTitles.length === 0
      ) {
        return { suggestedTitle: "", titleSuggestions: [] };
      }

      // Copy the array to avoid modifying the original
      const titles = [...planTemplateData.suggestedTitles];

      // Randomly pick one title
      const suggestedTitleIndex = Math.floor(Math.random() * titles.length);
      const suggestedTitle = titles[suggestedTitleIndex];

      // Return the rest of the suggestions (filter out the selected one)
      const titleSuggestions = titles.filter(
        (_, index) => index !== suggestedTitleIndex,
      );

      return { suggestedTitle, titleSuggestions };
    }, [planTemplateData]);

    const [isCreating, setIsCreating] = React.useState(false);
    const [title, setTitle] = React.useState(suggestedTitle);
    const [displayName, setDisplayName] = React.useState(
      sessionStore.superUser?.data.displayName ?? "",
    );
    const [currentTab, setCurrentTab] = React.useState<string>("title");

    const showFineTuneTab = false; // disabled for now !isQuickstart && !!modulesState.modules.length;

    React.useEffect(() => {
      if (planTemplateData) {
        const modulesFound = planTemplateData.sections[1].modules;

        runInAction(() => {
          if (modulesFound.length) {
            modulesState.modules =
              modulesFound.map((m, i) => ({ ...m, keep: true, index: i })) ??
              [];
          } else {
            modulesState.modules = [];
          }
        });
      }
    }, [planTemplateData, modulesState]);

    React.useEffect(() => {
      trackEvent(`create_tab_${currentTab}`);
    }, [currentTab]);

    const createPlanAction = React.useCallback(async () => {
      if (planTemplateData && title && displayName) {
        trackEvent("fine_tune_create_plan");
        setIsCreating(true);
        await createPlan(
          planTemplateData,
          modulesState.modules,
          title,
          displayName,
        );

        setIsCreating(false);
        setTitle("");
        setCurrentTab("title");
      }
    }, [
      planTemplateData,
      title,
      displayName,
      createPlan,
      modulesState.modules,
    ]);

    const nextStepProps = React.useMemo(() => {
      if (currentTab === "title") {
        return {
          icon: ChevronRight,
          label: "Next",
          disabled: !title,
          onClick: () =>
            setCurrentTab(showFineTuneTab ? "cards" : "displayName"),
        };
      }
      if (currentTab === "cards") {
        return {
          icon: ChevronRight,
          label: "Next",
          disabled: false,
          onClick: () => setCurrentTab("displayName"),
        };
      }

      return {
        label: "Start Planira!",
        disabled: !displayName || !title,
        icon: PartyPopper,
        onClick: createPlanAction,
      };
    }, [currentTab, title, displayName, createPlanAction]);

    const prevStepOnClick = React.useMemo(() => {
      if (currentTab === "title") {
        return;
      }

      if (currentTab === "cards") {
        return () => setCurrentTab("title");
      }

      return () =>
        showFineTuneTab ? setCurrentTab("cards") : setCurrentTab("title");
    }, [currentTab]);

    const onSubmit = React.useCallback(() => {
      if (currentTab === "displayName" && !!displayName && !!title) {
        createPlanAction();
      } else if (currentTab === "title" && !!title) {
        setCurrentTab("displayName");
      }
    }, [currentTab, title, displayName, createPlanAction]);

    if (!planTemplateData) {
      return null;
    }

    const imageSource = planTemplateData.imageType
      ? PlaniraImageLookUp[planTemplateData.imageType]
      : planTemplateData.image;

    return (
      <>
        {imageSource ? (
          <View>
            {!!imageSource && (
              <Image
                // @ts-expect-error
                source={imageSource}
                resizeMode="contain"
                width="100%"
                height={200}
              />
            )}
            <Box>
              <YStack flex={1} gap="$small">
                <XStack gap="$small" alignItems="center">
                  {!!planTemplateData.emoji && (
                    <Body>{planTemplateData.emoji}</Body>
                  )}
                  <Label
                    textTransform="uppercase"
                    alignItems="center"
                    flexDirection="row"
                  >
                    {planTemplateData.title}
                  </Label>
                </XStack>
                <Body>{planTemplateData.description}</Body>
              </YStack>
            </Box>
          </View>
        ) : (
          <Box gap="$small">
            <XStack gap="$space.small">
              <Sparkles />
              <Title>{t("home_start_planira_1")}</Title>
            </XStack>
            <Body>{planTemplateData.description}</Body>
          </Box>
        )}
        <View>
          {isCreating && (
            <View height={250} alignItems="center" justifyContent="center">
              <YStack gap="$medium">
                <ActivityIndicator size={"large"} color={"#ffd84e"} />
                <Body muted>Hold on!</Body>
              </YStack>
            </View>
          )}
          {!isCreating && (
            <FineTunePlanTabs
              plan={planTemplateData}
              modulesState={modulesState}
              currentTab={currentTab}
              onTabChange={setCurrentTab}
              title={title}
              titleSuggestions={titleSuggestions}
              setTitle={setTitle}
              displayName={displayName}
              setDisplayName={setDisplayName}
              onSubmit={onSubmit}
            />
          )}
          <XStack>
            {prevStepOnClick ? (
              <Button
                iconPosition="before"
                type="ghost"
                icon={ChevronLeft}
                onPress={prevStepOnClick}
              >
                Back
              </Button>
            ) : undefined}
            <Button
              style={{ flex: 1 }}
              disabled={isCreating || nextStepProps.disabled}
              icon={nextStepProps.icon}
              onPress={nextStepProps.onClick}
            >
              {nextStepProps.label}
            </Button>
          </XStack>
        </View>
      </>
    );
  });
